import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'

export default function Projects() {
  const [projects, setProjects] = useState([])
  const [activeCategory, setActiveCategory] = useState('Alle')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/projekter')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          setProjects(data.items)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const categories = ['Alle', 'Tømrerarbejde', 'Tag', 'Renovering']

  const filteredProjects = activeCategory === 'Alle'
    ? projects
    : projects.filter((p) => p.projektkategori === activeCategory)

  return (
    <>
      <SEOHead
        title="Projekter & Referencer - Agstner Byg ApS"
        description="Se udvalgte projekter udført af Agstner Byg ApS inden for tømrerarbejde, tagarbejde og boligrenovering i Vissenbjerg, Odense og på Fyn."
      />

      {/* Header */}
      <section className="relative py-20 bg-zinc-950 overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/bac61bd8-01e8-4025-a9be-38664430d08c/agstner-hero-carpentry.jpg"
            alt="Agstner Byg Projekter"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/85 via-zinc-950/50 to-zinc-950/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-orange-500 font-display text-xs uppercase tracking-widest mb-2">
            <span className="w-6 h-0.5 bg-orange-500"></span>
            <span>REFERENCER & HÅNDVÆRK</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-display font-black text-white uppercase tracking-tight">
            Vores udvalgte projekter
          </h1>
          <p className="mt-3 text-base sm:text-lg text-zinc-300 max-w-2xl">
            Gå på opdagelse i vores tidligere udførte opgaver for private boligejere på Fyn.
          </p>

          {/* Category Filter Pills (Industrial sharp tabs) */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-none text-xs font-display font-bold uppercase tracking-widest transition-all ${
                  activeCategory === cat
                    ? 'bg-orange-500 text-zinc-950 border border-orange-500 font-bold'
                    : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-20 bg-zinc-900 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-20 text-center text-zinc-500 font-mono text-xs uppercase">Henter projekter...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="py-20 text-center text-zinc-500 font-mono text-xs uppercase">
              Ingen projekter fundet i denne kategori.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((item, idx) => (
                <div
                  key={item.id}
                  className="bg-zinc-950 border border-zinc-800 overflow-hidden flex flex-col group hover:border-orange-500 transition-all"
                >
                  <div className="relative h-80 sm:h-96 overflow-hidden border-b border-zinc-800">
                    <img
                      src={
                        Array.isArray(item.hovedbillede) && item.hovedbillede[0]
                          ? item.hovedbillede[0]
                          : 'https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/agstner-hero-carpentry.jpg'
                      }
                      alt={item.projektnavn}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-zinc-950/90 text-orange-500 font-mono font-bold text-xs px-3 py-1 border border-zinc-800">
                      00{idx + 1} / {item.projektkategori}
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <span className="text-xs font-mono text-orange-500 uppercase flex items-center gap-1.5 mb-1">
                        <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                        {item.omraade || 'Fyn'}
                      </span>
                      <h3 className="font-display font-bold text-xl text-white uppercase group-hover:text-orange-500 transition-colors">
                        {item.projektnavn}
                      </h3>
                      <p className="text-zinc-400 text-xs mt-2 leading-relaxed line-clamp-3">
                        {item.kort_beskrivelse}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-zinc-800 flex items-center justify-between">
                      <Link
                        to={`/projekter/${item.url_slug || item.id}`}
                        className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:text-orange-400 flex items-center gap-1.5"
                      >
                        <span>Se komplet projekt</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Banner */}
          <div className="mt-20 bg-zinc-950 p-8 sm:p-12 border border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-display font-bold text-white uppercase">Har du en lignende byggeopgave?</h3>
              <p className="text-zinc-400 text-xs mt-1">
                Agstner Byg ApS giver gerne et uforpligtende tilbud på dit projekt.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="px-8 py-4 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all whitespace-nowrap border border-orange-500"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
